import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Button, ButtonTheme } from 'components/Button';
import { ICONS } from 'components/Icon';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageFlatList } from 'components/Page';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import { ListRenderItem } from '@shopify/flash-list';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  REMOTE_ASESETUPLIBRARY_QUESTIONOPEN,
  REMOTE_ASESETUP_COLLAPSEFOLDERSECTIONS,
  REMOTE_ASESETUP_FOLDERDELETE,
  REMOTE_ASESETUP_FOLDEROPEN,
  REMOTE_ASESETUP_SECTIONOPEN,
} from 'store/actions';
import {
  selectAseSetupLibraryFolder,
  selectAseSetupLibraryFolderItemList,
} from 'store/remote/selectors';
import { AssessmentQuestionType } from 'types/types';
import { SectionListItem } from './ListItems/SectionListItem';

// const renderItem1: ListRenderItem<
//   ReturnType<typeof selectAseSetupLibraryFolderItemList>[number]
// > = ({ item }) => {
//   const { type } = item;
//   if (type === 'addquestion') {
//     <View style={{ alignItems: 'center' }}>
//       <Button
//         text="New Question"
//         icon={ICONS.PlusLight}
//         buttonTheme={ButtonTheme.themeless}
//         textSize="small"
//         onPress={() => {}}
//       />
//     </View>;
//   }
//   if (type === 'addsection') {
//     <View style={{ alignItems: 'center' }}>
//       <Button
//         text="New Section"
//         icon={ICONS.PlusLight}
//         buttonTheme={ButtonTheme.themeless}
//         textSize="small"
//         onPress={() => dispatch(REMOTE_ASESETUP_SECTIONOPEN({ type.id }))}
//       />
//     </View>;
//   }
//   return <SectionListItem type={type} id={item.id} level={item.level} />;
// };

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSESSMENTSETUPLIBRARYEXPLORER],
  Screens.ASSESSMENTSETUPLIBRARYEXPLORERSECTIONS
>;

export const SectionListScreen: React.FC<Props> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const { id: folderId } = route.params;
  const folder = useSelector((state) =>
    selectAseSetupLibraryFolder(state, folderId),
  );
  const items = useSelector((state) =>
    selectAseSetupLibraryFolderItemList(state, folderId),
  );

  const handleCreateSection = () =>
    dispatch(REMOTE_ASESETUP_SECTIONOPEN({ folderId }));

  React.useEffect(() => {
    if (!folder?.id) {
      navigation.goBack();
    }
  }, [folder?.id, navigation]);

  // ListRenderItem<ReturnType<typeof selectAseSetupLibraryFolderItemList>[number]>

  const renderItem: ListRenderItem<
    ReturnType<typeof selectAseSetupLibraryFolderItemList>[number]
  > = React.useCallback(
    ({ item }) => {
      const { type } = item;
      if (type === 'addquestion') {
        return (
          <ViewWithSeparators
            style={{
              padding: theme.paddingHorizontal,
              justifyContent: 'center',
            }}
            horizontal
          >
            <Button
              text="New Checklist"
              icon={ICONS.CircleCSolid}
              iconColor={theme.colors.checklist}
              buttonTheme={ButtonTheme.themeless}
              compact
              onPress={() =>
                dispatch(
                  REMOTE_ASESETUPLIBRARY_QUESTIONOPEN({
                    sectionId: item.id,
                    type: AssessmentQuestionType.checklist,
                  }),
                )
              }
            />
            <Button
              text="New Question"
              icon={ICONS.CircleQSolid}
              iconColor={theme.colors.question}
              buttonTheme={ButtonTheme.themeless}
              compact
              onPress={() =>
                dispatch(
                  REMOTE_ASESETUPLIBRARY_QUESTIONOPEN({
                    sectionId: item.id,
                    type: AssessmentQuestionType.question,
                  }),
                )
              }
            />
          </ViewWithSeparators>
        );
      }
      if (type === 'addsection') {
        return (
          <View
            style={{ alignItems: 'center', padding: theme.paddingHorizontal }}
          >
            <Button
              text="New Section"
              buttonTheme={ButtonTheme.themeless}
              compact
              onPress={() =>
                dispatch(REMOTE_ASESETUP_SECTIONOPEN({ folderId: item.id }))
              }
            />
          </View>
        );
      }
      return <SectionListItem type={type} id={item.id} level={item.level} />;
    },
    [dispatch],
  );

  if (!folder) {
    return null;
  }

  return (
    <PageFlatList
      isInModal={false}
      header={
        <NavigationHeader
          heading="Checklists & Questions"
          subheading={folder.name}
          onPressBackButton={navigation.goBack}
          menuRight={{
            iconButtonConfig: {
              icon: ICONS.EllipsisLight,
            },
            options: [
              {
                text: 'Rename Folder',
                icon: ICONS.PencilLight,
                onPress: () =>
                  dispatch(REMOTE_ASESETUP_FOLDEROPEN({ id: folderId })),
              },
              {
                text: 'Delete Folder',
                icon: ICONS.TrashCanLight,
                destructive: true,
                onPress: () =>
                  dispatch(REMOTE_ASESETUP_FOLDERDELETE({ id: folderId })),
              },
              '-',
              {
                text: 'New Section',
                onPress: handleCreateSection,
                icon: ICONS.PlusLight,
              },
              {
                text: 'Expand Sections',
                onPress: () =>
                  dispatch(
                    REMOTE_ASESETUP_COLLAPSEFOLDERSECTIONS({
                      folderId,
                      collapse: false,
                    }),
                  ),
              },
              {
                text: 'Collapse Sections',
                onPress: () =>
                  dispatch(
                    REMOTE_ASESETUP_COLLAPSEFOLDERSECTIONS({
                      folderId,
                      collapse: true,
                    }),
                  ),
              },
            ],
          }}
        />
      }
      emptyConfig={{
        icon: ICONS.RBPlectrumLight,
        button: { text: 'New Section', onPress: handleCreateSection },
      }}
      listConfig={{
        data: items,
        keyExtractor: (item) => `${item.type}-${item.id}`,
        renderItem,
        ItemSeparatorComponent: SeparatorHorizontal,
      }}
    />
  );
};
